import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DEFAULT_LIMIT_PAGE } from '@/constants'
import { formatCurrency, useParamsState } from '@/helpers'
import { useRouteList, useTransporCompanyRoutesList } from '@/services'
import {
  Button,
  CloseIcon,
  FilterIcon,
  InputSearch,
  Pagination,
  Select,
  TableColumns,
  TableColumnsColumnProps,
  Tag,
  Text,
} from '@mercai/clever'

import { EmptyState } from '@/components'
import { TransportCompanyRoutesDrawerFilter } from './components'

import { TransportCompanyRouteResponseProps } from '@/types'
import { TableLogisticRoutesProps } from './table-logistic-routes.types'

import styles from './table-logistic-routes.module.css'

export const TableLogisticRoutes = ({
  transportCompanyUuid,
  withCarrier = false,
  isImporter,
}: TableLogisticRoutesProps) => {
  const { t } = useTranslation('screenLogistic')

  const [filter, setFilter] = useParamsState({
    transportCompany_Uuid: transportCompanyUuid || '',
    page: '1',
    limit: `${DEFAULT_LIMIT_PAGE}`,
    logisticRoute_Uuid: 'all',
    search: '',
  })

  const [totalPages, setTotalPages] = useState(1)

  const [isOpenDrawerFilter, setIsOpenDrawerFilter] = useState(false)

  const handleFilter = (changedKey: string, newValue: string) => {
    setFilter({
      ...filter,
      ...(changedKey === 'search' ? { page: '1' } : {}),
      [changedKey]: newValue,
    })
  }

  const { data, isLoading } = useTransporCompanyRoutesList({
    params: {
      ...filter,
      limit: filter?.limit || `${DEFAULT_LIMIT_PAGE}`,
      page: filter?.page || `1`,
      logisticRoute_Uuid:
        filter.logisticRoute_Uuid !== 'all' ? filter.logisticRoute_Uuid : '',
    },
  })
  const { data: dataRoutes } = useRouteList({
    params: {
      ...(isImporter !== undefined
        ? isImporter
          ? { isForeign: 'true' }
          : { isForeign: 'false' }
        : {}),
    },
  })

  const getFormattedFilter = (filterKey: string | number) => {
    const filterString = filter?.[filterKey]

    if (filterKey === 'logisticRoute_OriginLocation') {
      return `${t('transportCompaniesRoutesFilterOriginLabel')}: ${filterString}`
    }

    if (filterKey === 'logisticRoute_DestinationLocation') {
      return `${t('transportCompaniesRoutesFilterDestinationLabel')}: ${filterString}`
    }

    if (filterKey === 'vehicle_Name') {
      return `${t('transportCompaniesRoutesFilterVehicleLabel')}: ${filterString}`
    }

    if (filterKey === 'isActive') {
      return `${t('transportCompaniesRoutesFilterIsActive')}: ${!filterString ? t('transportCompaniesRoutesFilterIsActiveAll') : filterString === 'true' ? t('transportCompaniesRoutesFilterIsActiveYes') : t('transportCompaniesRoutesFilterIsActiveNo')}`
    }

    if (!filterString || typeof filterString === 'number') return ''

    if (filterString?.[0] === ',') {
      return filterString?.slice(1)?.replace(/,/g, ', ')
    }

    return filterString?.replace(/,/g, ', ')
  }

  const getContentFilter = () => {
    const filtersKeys = Object.keys(filter).filter(
      (item) =>
        ![
          'limit',
          'page',
          'ordering',
          'tab',
          'logisticRoute_Uuid',
          'transportCompany_Uuid',
        ].includes(item) && !!filter[item],
    )

    if (filtersKeys && filtersKeys.length === 0) {
      return null
    }

    return (
      <div className={styles['filtered-options']}>
        <Text>{t('transportCompaniesRoutesFilterApplied')}:</Text>

        {filtersKeys?.map((item) => (
          <Button
            onClick={() => handleFilter(item, '')}
            variant="light"
            size="small"
            key={item}
          >
            {getFormattedFilter(item)}

            <CloseIcon />
          </Button>
        ))}
      </div>
    )
  }

  const getContentTotalFiltered = () => {
    const activeFilters = Object.keys(filter)?.filter(
      (key) =>
        !!filter[key] &&
        key !== 'page' &&
        key !== 'limit' &&
        key !== 'tab' &&
        key !== 'transportCompany_Uuid' &&
        key !== 'logisticRoute_Uuid',
    ).length

    if (activeFilters > 0) {
      return (
        <Tag variant="primary" model="light" size="small">
          {activeFilters}
        </Tag>
      )
    }

    return ''
  }

  const columns: TableColumnsColumnProps<TransportCompanyRouteResponseProps>[] =
    [
      ...(withCarrier
        ? [
            {
              title: t('transportCompaniesRoutesTableTransportCompanyName'),
              key: 'transportCompany.name',
              render: ({
                record,
              }: {
                record: TransportCompanyRouteResponseProps
              }) => record?.transportCompany?.name,
            },
          ]
        : []),
      {
        title: t('transportCompaniesRoutesTableName'),
        key: 'name',
        dataKey: 'logisticRoute',
        render: ({ record }) => record?.logisticRoute?.name,
      },
      {
        title: t('transportCompaniesRoutesTableIsActive'),
        key: 'isActive',
        dataKey: 'isActive',
        width: '10rem',
        render: ({ record }) =>
          record?.isActive
            ? t('transportCompaniesRoutesTableIsActiveYes')
            : t('transportCompaniesRoutesTableIsActiveNo'),
      },
      {
        title: t('transportCompaniesRoutesTableVehicle'),
        key: 'vehicle.name',
        dataKey: 'vehicle.name',
        width: '12rem',
        render: ({ record }) => record?.vehicle?.name,
      },
      {
        title: t('transportCompaniesRoutesTableCapacity'),
        key: 'vehicle.capacity',
        dataKey: 'vehicle.capacity',
        width: '12rem',
        render: ({ record }) => record?.vehicle?.capacity,
      },
      {
        title: t('transportCompaniesRoutesTablePrice'),
        key: 'price',
        dataKey: 'price',
        width: '12rem',
        render: ({ record }) =>
          formatCurrency(
            record?.price || '',
            'es-MX',
            record?.currency || 'MXN',
          ),
      },
      {
        title: t('transportCompaniesRoutesTableInsurancePrice'),
        key: 'insurancePrice',
        dataKey: 'insurancePrice',
        width: '12rem',
        render: ({ record }) =>
          record?.insurancePrice
            ? formatCurrency(
                record?.insurancePrice || '',
                'es-MX',
                record?.currency || 'MXN',
              )
            : '-',
      },
      {
        title: t('transportCompaniesRoutesTableCrossDockingPrice'),
        key: 'crossDockingPrice',
        dataKey: 'crossDockingPrice',
        width: '12rem',
        render: ({ record }) =>
          record?.crossDockingPrice
            ? formatCurrency(
                record?.crossDockingPrice || '',
                'es-MX',
                record?.currency || 'MXN',
              )
            : '-',
      },
    ]

  useEffect(() => {
    if (data?.data?.count) {
      setTotalPages(
        Math.ceil(data?.data?.count / (+filter.limit || +DEFAULT_LIMIT_PAGE)),
      )
    }
  }, [data])

  return (
    <div>
      {withCarrier && (
        <Text className={styles.title} variant="title-large">
          {t('transportCompaniesRoutesTitle')}
        </Text>
      )}
      <div className={styles.filters}>
        <div className={styles['filter-group']}>
          <InputSearch
            value={filter.search as string}
            onChange={(value) => handleFilter('search', value)}
            className={styles['search-input']}
            translations={{
              inputPlaceholder: t('searchFilterPlaceholder'),
            }}
          />
        </div>

        <Select
          options={[
            {
              label: t('transportCompaniesRoutesFilterRoutes'),
              value: 'all',
            },
            ...(dataRoutes?.data?.results?.map((route) => ({
              label: route.name,
              value: route.uuid,
            })) || []),
          ]}
          value={filter.logisticRoute_Uuid}
          onChange={(value) => handleFilter('logisticRoute_Uuid', value)}
        />

        <Button variant="secondary" onClick={() => setIsOpenDrawerFilter(true)}>
          <FilterIcon />
          {t('transportCompaniesRoutesFilterTitle')}
          {getContentTotalFiltered()}
        </Button>
      </div>

      {getContentFilter()}

      <TableColumns<TransportCompanyRouteResponseProps>
        isLoading={isLoading}
        columns={columns}
        data={data?.data?.results || []}
      />

      {!isLoading && data?.data?.results?.length === 0 && (
        <EmptyState title={t('tableEmptyTitle')} description={''} />
      )}

      <div className={styles.pagination}>
        <div className={styles['pagination-show-registers']}>
          <Text>
            {t('tableShowRegisters', {
              showNumber:
                (data?.data?.count || 0) < +filter.limit
                  ? data?.data?.count
                  : +filter.limit,
              totalNumber: data?.data?.count || 0,
            })}
          </Text>

          <Select
            value={filter.limit}
            onChange={(value) => handleFilter('limit', value)}
            options={[
              { label: '10', value: '10' },
              { label: '20', value: '20' },
              { label: '50', value: '50' },
              { label: '100', value: '100' },
            ]}
          />
        </div>

        <Pagination
          totalPages={totalPages}
          onChange={(page) => handleFilter('page', `${page}`)}
          currentPage={(+filter.page || 1) as number}
        />
      </div>

      <TransportCompanyRoutesDrawerFilter
        isOpen={isOpenDrawerFilter}
        onHandleOpen={() => setIsOpenDrawerFilter((prev) => !prev)}
        filter={filter}
        onHandleFilter={setFilter}
      />
    </div>
  )
}
